<template>
  <div class="rule-card" :class="{ incomplete: !hideProduct && !isComplete }">
    <div v-if="!hideProduct" class="rule-head">
      <div class="rule-product">
        <label class="field-label">Product</label>
        <multiselect
          :value="productOption"
          class="rm-select"
          :options="availableProducts"
          :custom-label="productLabel"
          track-by="productId"
          placeholder="— select a product —"
          :show-labels="false"
          :allow-empty="true"
          :max-height="360"
          @input="setProduct"
        />
      </div>
      <button class="icon-remove" title="Remove rule" @click="$emit('remove')">
        <i class="fas fa-trash"></i>
      </button>
    </div>

    <div class="rule-modes">
      <button
        v-for="m in modes"
        :key="m.value"
        class="mode-btn"
        :class="{ active: rule.mode === m.value }"
        @click="setMode(m.value)"
      >
        {{ m.label }}
      </button>
    </div>

    <!-- Switch Plan / Promo: target product -->
    <div v-if="rule.mode !== 'REGULAR'" class="field">
      <label class="field-label">Target product</label>
      <multiselect
        :value="targetOption"
        class="rm-select"
        :options="targetOptions"
        :custom-label="productLabel"
        track-by="productId"
        :placeholder="rule.mode === 'PROMOTIONAL_OFFER' ? '— product the offer applies to —' : '— product to switch to —'"
        :show-labels="false"
        :allow-empty="true"
        :max-height="320"
        @input="setTarget"
      />
      <p v-if="rule.mode === 'SWITCH_PLAN' && rule.targetProductId && rule.targetProductId === rule.productId" class="field-warn">
        Switch Plan can't target the rule's own product — the service treats this as a miss and falls back to Apple's default.
      </p>
    </div>

    <!-- Promo: offer -->
    <div v-if="rule.mode === 'PROMOTIONAL_OFFER'" class="field">
      <label class="field-label">Promotional offer</label>
      <multiselect
        :value="offerOption"
        class="rm-select"
        :options="targetOffers"
        :custom-label="offerLabel"
        track-by="id"
        :placeholder="rule.targetProductId ? '— offer on target product —' : 'select a target product first'"
        :show-labels="false"
        :allow-empty="true"
        :disabled="!rule.targetProductId"
        :max-height="320"
        @input="setOffer"
      />
      <p v-if="rule.targetProductId && targetOffers.length === 0" class="field-warn">
        Target product has no promotional offers in the catalog.
      </p>
      <p v-else-if="offerOption && offerOption.source !== 'RETENTION_MESSAGING'" class="field-warn">
        This offer's source is <code>{{ offerOption.source || '—' }}</code>, not <code>RETENTION_MESSAGING</code>. Double-check it's meant for realtime retention.
      </p>
    </div>

    <!-- Message -->
    <div class="field">
      <label class="field-label">Message</label>
      <locale-message-picker
        :value="rule.message"
        :approved-messages="approvedMessages"
        :groups="groups"
        :members-by-group-id="membersByGroupId"
        @input="setMessage"
      />
    </div>

    <p v-if="!hideProduct && !isComplete" class="rule-incomplete-note">
      <i class="fas fa-info-circle"></i> Incomplete — this rule is excluded from the JSON until {{ missingLabel }}.
    </p>
  </div>
</template>

<script>
import LocaleMessagePicker from './LocaleMessagePicker.vue';
import { messageIdentifierValue, ruleComplete } from './realtimeRule';

export default {
  name: 'RealtimeRuleCard',
  components: { LocaleMessagePicker },
  props: {
    rule: { type: Object, required: true },
    products: { type: Array, default: () => [] },
    approvedMessages: { type: Array, default: () => [] },
    // For the picker's "Fill from a group" shortcut.
    groups: { type: Array, default: () => [] },
    membersByGroupId: { type: Object, default: () => ({}) },
    usedProductIds: { type: Array, default: () => [] },
    // When true the product selector + completeness note are hidden, so the card
    // acts as a reusable "message shape" editor (used by the bulk-add panel).
    hideProduct: { type: Boolean, default: false }
  },
  data() {
    return {
      modes: [
        { value: 'REGULAR', label: 'Regular' },
        { value: 'SWITCH_PLAN', label: 'Switch Plan' },
        { value: 'PROMOTIONAL_OFFER', label: 'Promo Offer' }
      ]
    };
  },
  computed: {
    availableProducts() {
      // Each productId can key the JSON once: hide products used by other rules,
      // but keep this rule's own current selection visible.
      const taken = new Set(this.usedProductIds.filter((id) => id !== this.rule.productId));
      return this.products.filter((p) => !taken.has(p.productId));
    },
    productOption() {
      return this.products.find((p) => p.productId === this.rule.productId) || null;
    },
    targetOption() {
      return this.products.find((p) => p.productId === this.rule.targetProductId) || null;
    },
    targetOptions() {
      // Switch Plan can't target the rule's own product (service rejects it as a miss).
      // Promo offers MAY target the same product (a discount on the current plan).
      if (this.rule.mode === 'SWITCH_PLAN') {
        return this.products.filter((p) => p.productId !== this.rule.productId);
      }
      return this.products;
    },
    targetProduct() {
      return this.products.find((p) => p.productId === this.rule.targetProductId) || null;
    },
    targetOffers() {
      const offers = this.targetProduct && this.targetProduct.promotional_offers;
      if (!offers || typeof offers !== 'object') return [];
      return Object.keys(offers).map((id) => ({ id, ...offers[id] }));
    },
    offerOption() {
      return this.targetOffers.find((o) => o.id === this.rule.offerIdentifier) || null;
    },
    hasMessage() {
      return !!messageIdentifierValue(this.rule.message);
    },
    isComplete() {
      return ruleComplete(this.rule);
    },
    missingLabel() {
      const missing = [];
      if (!this.rule.productId) missing.push('a product');
      if (this.rule.mode !== 'REGULAR' && !this.rule.targetProductId) missing.push('a target product');
      if (this.rule.mode === 'PROMOTIONAL_OFFER' && !this.rule.offerIdentifier) missing.push('an offer');
      if (!this.hasMessage) missing.push('a message');
      return missing.join(', ');
    }
  },
  methods: {
    productLabel(p) {
      return p.productId;
    },
    offerLabel(o) {
      const bits = [o.id, o.offer_type];
      if (o.offer_type !== 'FREE_TRIAL' && o.price != null) bits.push(`$${o.price}`);
      if (o.duration) bits.push(o.duration);
      if (o.number_of_periods) bits.push(`×${o.number_of_periods}`);
      if (o.source) bits.push(o.source);
      return bits.filter(Boolean).join(' · ');
    },
    update(patch) {
      this.$emit('input', { ...this.rule, ...patch });
    },
    setProduct(p) {
      this.update({ productId: p ? p.productId : null });
    },
    setMode(mode) {
      if (mode === this.rule.mode) return;
      const patch = { mode };
      if (mode === 'REGULAR') { patch.targetProductId = null; patch.offerIdentifier = null; }
      if (mode === 'SWITCH_PLAN') {
        patch.offerIdentifier = null;
        // A same-product target is invalid for Switch Plan — drop it on entry.
        if (this.rule.targetProductId === this.rule.productId) patch.targetProductId = null;
      }
      this.update(patch);
    },
    setTarget(p) {
      // Changing target invalidates the previously chosen offer.
      this.update({ targetProductId: p ? p.productId : null, offerIdentifier: null });
    },
    setOffer(o) {
      this.update({ offerIdentifier: o ? o.id : null });
    },
    setMessage(model) {
      this.update({ message: model });
    }
  }
};
</script>

<style scoped>
.rule-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.9rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}
.rule-card.incomplete {
  border-color: rgba(255, 184, 77, 0.35);
}

.rule-head {
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
}
.rule-product { flex: 1; }

.field { display: flex; flex-direction: column; gap: 0.3rem; }

.field-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.rule-modes {
  display: inline-flex;
  gap: 0.4rem;
}
.mode-btn {
  padding: 5px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}
.mode-btn:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }
.mode-btn.active { background: #03B3DA; border-color: #03B3DA; color: #fff; }

.field-warn {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #ffb84d;
}
.field-warn code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  background: rgba(255, 255, 255, 0.08);
  padding: 1px 5px;
  border-radius: 3px;
  color: #fff;
}

.rule-incomplete-note {
  margin: 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
}

.icon-remove {
  background: none;
  border: 1px solid rgba(231, 76, 60, 0.4);
  color: #e74c3c;
  border-radius: 6px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  flex-shrink: 0;
}
.icon-remove:hover { background: rgba(231, 76, 60, 0.12); }

/* vue-multiselect dark theme */
.rm-select ::v-deep .multiselect__tags {
  min-height: 36px;
  padding: 6px 40px 0 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
}
.rm-select ::v-deep .multiselect__single,
.rm-select ::v-deep .multiselect__input {
  background: transparent;
  color: #fff;
  font-size: 0.85rem;
  margin-bottom: 6px;
  padding: 0;
}
.rm-select ::v-deep .multiselect__placeholder {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.85rem;
  margin-bottom: 6px;
  padding-top: 0;
}
/* --active / --disabled live on the multiselect ROOT (== the .rm-select element),
   so they must be matched as deep descendants of the card, not under .rm-select. */
::v-deep .multiselect--active .multiselect__tags {
  border-color: #03B3DA;
  box-shadow: 0 0 0 0.15rem rgba(3, 179, 218, 0.2);
}
/* Disabled (e.g. offer select before a target is picked): keep the dark theme
   with a readable, dimmed placeholder instead of vue-multiselect's light-grey default. */
::v-deep .multiselect--disabled {
  background: transparent;
  opacity: 1;
  border-radius: 6px;
}
::v-deep .multiselect--disabled .multiselect__tags {
  background: rgba(255, 255, 255, 0.02);
  border-style: dashed;
  border-color: rgba(255, 255, 255, 0.1);
}
::v-deep .multiselect--disabled .multiselect__select { background: transparent; }
::v-deep .multiselect--disabled .multiselect__single,
::v-deep .multiselect--disabled .multiselect__placeholder { color: rgba(255, 255, 255, 0.3); }
.rm-select ::v-deep .multiselect__content-wrapper {
  background: #1a2332;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.rm-select ::v-deep .multiselect__option {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  white-space: normal;
  min-height: unset;
  padding: 8px 12px;
}
.rm-select ::v-deep .multiselect__option--highlight,
.rm-select ::v-deep .multiselect__option--highlight::after {
  background: #03B3DA;
  color: #fff;
}
.rm-select ::v-deep .multiselect__option--selected {
  background: rgba(3, 179, 218, 0.18);
  color: #fff;
  font-weight: 600;
}
.rm-select ::v-deep .multiselect__option--selected.multiselect__option--highlight {
  background: #03B3DA;
}
</style>
